/* eslint-disable */
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";
import lockr from 'lockr';
import { notification } from 'antd';
import { isUndefined } from 'underscore';
import { persistor } from "../store";
import { logoutUser } from "../user/userApi";
import NotFound from '../../pages/NotFound';
import constants, { Roles } from '../constants';
import { AppDispatch, CombinedStates, ProtectedRouteProps } from '../types';

interface AuthRouteItem {
    path: string;
    component: React.ComponentType<any>;
    type: 'auth';
    allow: Roles[];
}

interface PublicRouteItem {
    path: string;
    component: React.ComponentType<any>;
    type: 'default';
}

type RouteItem = AuthRouteItem | PublicRouteItem;

interface CoreRouterProps {
    routes: RouteItem[];
}

interface LogoutRouteProps {
    path: string;
    exact: boolean;
    persistor: any; // Replace 'any' with the correct type for your persistor
}

const {
    AUTH_KEY,
    LOCALSTORAGE_EXPIRES_KEY,
    LOCALSTORAGE_EXPIRES_TIME,
} = constants;

const onlyAuthorisedAllowed = ():boolean => {
    const response = lockr.get(AUTH_KEY);
    if (!isUndefined(response)) {
        const authDate:string = lockr.get(LOCALSTORAGE_EXPIRES_KEY);
        if (!isUndefined(authDate)) {
            const parsedAuthDate = new Date(authDate);
            const now = new Date();
            const differenceMilliseconds = now.getTime() - parsedAuthDate.getTime();
            const differenceMinutes = Math.floor(differenceMilliseconds / 1000 / 60);
            if (differenceMinutes >= LOCALSTORAGE_EXPIRES_TIME) {
                notification.error({ message: 'Error', description: 'Session expired. Please login again.' });
                return false;
            }
        } else {
            notification.error({ message: 'Error', description: 'Session expired. Please login again.' });
            return false;
        }
        return true;
    }
    return false;
};

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const authorised = onlyAuthorisedAllowed();

    // Run the logout dispatch as a post-commit effect so we don't dispatch a
    // Redux action during render. The conditional return below stays valid
    // because both branches now run after the same hook calls.
    React.useEffect(() => {
        if (!authorised) {
            dispatch(logoutUser());
        }
    }, [authorised, dispatch]);

    return authorised ? <>{children}</> : <Navigate to="/" />;
};

// Per-route role enforcement.
//
// TRUST BOUNDARY — this is a UX/discoverability boundary, NOT a security
// boundary. The role string we read here comes from Redux, which is hydrated
// from localStorage by redux-persist (see core/store.ts). Any attacker with
// browser access can edit localStorage and set their role to 'admin' — the
// guard will then happily render admin-only routes. The real defence is the
// server-side role gates on each Laravel controller (UserController,
// NewsArticleController, EmergencyServiceController on chore/PentestFix).
// Do not weaken those gates on the assumption that the Portal defends itself.
//
// Sits inside ProtectedRoute so the session check runs first and an expired
// session redirects to / before this component is reached.
const AuthedRoute = ({ route }: { route: AuthRouteItem }) => {
    const roleName = useSelector((state: CombinedStates) => state.user.userData?.role?.name);

    // Empty role means redux hasn't hydrated yet (initial state on userSlice.ts
    // sets role.name to ''). Render nothing rather than flash a redirect.
    if (!roleName) {
        return null;
    }

    // Direct string equality — Roles enum values are strings, so no cast is
    // needed and an unknown future role from the API will correctly fail to
    // match and trigger the redirect below.
    if (!route.allow.some((r) => r === roleName)) {
        return <Navigate to="/dashboard" replace />;
    }

    const Component = route.component;
    return <Component />;
};

const cleanUpLocalStorage = () => {
    lockr.rm(AUTH_KEY);
    lockr.rm(LOCALSTORAGE_EXPIRES_KEY);

    localStorage.clear();
    lockr.flush();
    persistor.flush();
    persistor.purge();
    window.location.href = '/';
    return null;
};

const LogoutRoute:React.FC<LogoutRouteProps> = ({ exact, path, persistor }) => {
    return cleanUpLocalStorage();
}

const CoreRouter: React.FC<CoreRouterProps> = ({ routes }) => (
    <Routes>
        {routes.map((route, index) => {
                let element;
                if (route.type === 'auth') {
                    element = (
                        <ProtectedRoute>
                            <AuthedRoute route={route} />
                        </ProtectedRoute>
                    );
                } else {
                    const Component = route.component;
                    element = <Component />;
                }
                return <Route
                    key={index}
                    path={route.path}
                    element={element} />;
            }
        )}
        <Route element={<NotFound />} />
    </Routes>
);

export {
    CoreRouter,
    onlyAuthorisedAllowed,
    LogoutRoute,
};